import { IVirtualManifest } from '@wix/editor-platform-manifest-services';

import { PrivateAPINamespace } from '../PrivateAPINamespace';
import { BlocksMigrationNamespaceContext } from './BlocksMigrationNamespaceContext';

export class BlocksMigrationNamespace extends PrivateAPINamespace {
  static readonly namespace = 'blocksMigration';

  async removeElement(
    manifestContextId: string,
    selector: string,
  ): Promise<void> {
    const query = this.#getContext(manifestContextId, selector).getQuery();

    await query.setStyles({
      cssProperties: {
        display: 'none',
      },
    });
  }

  async restoreElement(
    manifestContextId: string,
    selector: string,
  ): Promise<void> {
    const query = this.#getContext(manifestContextId, selector).getQuery();

    await query.setStyles({
      cssProperties: {
        display: 'block',
      },
    });
  }

  async isRemoved(
    manifestContextId: string,
    selector: string,
  ): Promise<boolean> {
    const query = this.#getContext(manifestContextId, selector).getQuery();

    const styles = await query.getStyles();

    return styles.cssProperties.display === 'none';
  }

  async listRemovedElements(manifestContextId: string): Promise<string[]> {
    const runtimeContext =
      this.ps.manifestServicesHost.getContextualManifest(manifestContextId);

    const removedElements: string[] = [];

    if (!runtimeContext?.tree) {
      return removedElements;
    }

    await this.#traverseTreeAsync(runtimeContext.tree, async (node, path) => {
      const api = this.ps.manifestServicesHost.getContextualManifestAPI(
        manifestContextId,
        path,
      );

      const styles = await api.query.getStyles();

      if (styles?.cssProperties?.display === 'none') {
        const selector = node.editorPointer?.innerElementPath?.at(-1);

        if (selector) {
          removedElements.push(selector);
        }
      }
    });

    return removedElements;
  }

  getChildPath(manifestContextId: string, selector: string): number[] {
    const virtualManifest =
      this.ps.manifestServicesHost.getContextualManifest(manifestContextId);

    if (!virtualManifest) {
      return [];
    }

    return this.#findChildPath(virtualManifest.tree.children, selector);
  }

  #findChildPath(
    children: IVirtualManifest['tree']['children'],
    target: string,
    acc: number[] = [],
  ): number[] {
    for (let i = 0; i < children.length; i++) {
      const child = children[i];

      if (child.editorPointer?.innerElementPath?.at(-1) === target) {
        return [...acc, i];
      }

      if (child.children?.length) {
        const result = this.#findChildPath(child.children, target, [...acc, i]);

        if (result.length) {
          return result;
        }
      }
    }

    return [];
  }

  async #traverseTreeAsync(
    node: IVirtualManifest['tree'],
    visitCb: (node: IVirtualManifest['tree'], path: number[]) => Promise<void>,
    path: number[] = [],
  ): Promise<void> {
    await visitCb(node, path);

    const children = node.children ?? [];
    children.forEach(async (child, index) => {
      await this.#traverseTreeAsync(child, visitCb, [...path, index]);
    });
  }

  #getContext(
    manifestContextId: string,
    selector: string,
  ): BlocksMigrationNamespaceContext {
    /**
     * TODO: the selecting child path logic should already be migrated
     * to virtualElement.getChild(...) by string
     */
    const childPath = this.getChildPath(manifestContextId, selector);

    return new BlocksMigrationNamespaceContext(
      this.ps,
      manifestContextId,
      childPath,
    );
  }
}
